import { useState, useEffect, useCallback, useRef } from 'react';

// Scale weight message from the gateway (also used for print_trigger events)
interface ScaleWeightMessage {
  type: 'weight' | 'print_trigger';
  station_id: number;
  weight: number;
  unit: 'lb' | 'kg' | 'oz' | 'g';
  seq_id?: string | number;
  ts?: string;
}

// Acknowledgment message from the gateway
interface ScaleAckMessage {
  type: 'ack';
  status: 'ok' | 'error' | 'duplicate';
  seq_id?: string | number;
  error?: string;
  ts?: string;
}

type ScaleMessage = ScaleWeightMessage | ScaleAckMessage;

// Connection status
export type ScaleConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

// Hook options
interface UseScaleWeightOptions {
  stationId?: number;           // Filter weights by station ID
  autoConnect?: boolean;        // Auto-connect on mount (default: true)
  reconnectAttempts?: number;   // Max reconnection attempts (default: 10)
  reconnectInterval?: number;   // Reconnection interval in ms (default: 3000)
  onPrintTrigger?: (weight: number, unit: string) => void; // Callback when scale's physical Print button is pressed
}

// Hook return type
interface UseScaleWeightReturn {
  weight: number;                      // Current weight value
  unit: string;                        // Weight unit (lb, kg, etc.)
  status: ScaleConnectionStatus;       // Connection status
  lastUpdate: Date | null;             // Last weight update timestamp
  error: string | null;                // Error message if any
  connect: () => void;                 // Manual connect function
  disconnect: () => void;              // Manual disconnect function
  tare: () => void;                    // Tare/zero the scale (sends command)
  isStable: boolean;                   // Weight is stable (not fluctuating)
  requestWeight: (targetWeight: number) => void; // Request simulated weight from scale
  sendMessage: (msg: Record<string, unknown>) => void; // Send arbitrary JSON to gateway
  setWeightRange: (low: number, high: number) => void; // Send PMW ABS range to scale display
  clearWeightRange: () => void; // Clear PMW ABS range on scale display
  enablePrintButton: () => void; // Subscribe to scale's physical Print button (ST 1)
  disablePrintButton: () => void; // Unsubscribe from scale's Print button (ST 0)
  setPresetTare: (weight: number, unit?: string) => void; // Set preset tare on scale (TA command)
  clearPresetTare: () => void; // Clear preset tare on scale (TAC command)
}

// Default WebSocket URL - can be overridden via environment variable
const getScaleGatewayUrl = (): string => {
  // Check for environment variable first
  if (import.meta.env.VITE_SCALE_GATEWAY_WS_URL) {
    return import.meta.env.VITE_SCALE_GATEWAY_WS_URL;
  }
  // Use same origin (works for both local dev and deployed environments)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host; // includes port if non-default (e.g. localhost:3000)
  return `${protocol}//${host}/ws/scale`;
};

export function useScaleWeight(options: UseScaleWeightOptions = {}): UseScaleWeightReturn {
  const {
    stationId,
    autoConnect = true,
    reconnectAttempts = 10,
    reconnectInterval = 3000,
    onPrintTrigger,
  } = options;

  // State
  const [weight, setWeight] = useState<number>(0);
  const [unit, setUnit] = useState<string>('lb');
  const [status, setStatus] = useState<ScaleConnectionStatus>('disconnected');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStable, setIsStable] = useState<boolean>(true);

  // Refs for WebSocket and reconnection
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastWeightsRef = useRef<number[]>([]); // For stability detection
  const isMountedRef = useRef<boolean>(true); // Track if component is mounted
  const onPrintTriggerRef = useRef(onPrintTrigger); // Ref so callback changes don't cause reconnects

  // Stability detection - check if last N readings are similar
  const checkStability = useCallback((newWeight: number) => {
    const readings = lastWeightsRef.current;
    readings.push(newWeight);
    if (readings.length > 5) readings.shift(); // Keep last 5 readings

    if (readings.length < 3) {
      setIsStable(false);
      return;
    }

    // Check if all readings are within 0.1 lb of each other
    const min = Math.min(...readings);
    const max = Math.max(...readings);
    setIsStable(max - min < 0.1);
  }, []);

  // Keep print trigger ref in sync with latest callback
  useEffect(() => {
    onPrintTriggerRef.current = onPrintTrigger;
  }, [onPrintTrigger]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const url = getScaleGatewayUrl();
    const clientId = stationId || 'frontend';
    const token = localStorage.getItem('authToken');
    const separator = url.includes('?') ? '&' : '?';
    const fullUrl = `${url}${separator}client_id=${encodeURIComponent(String(clientId))}${token ? `&access_token=${encodeURIComponent(token)}` : ''}`;

    setStatus('connecting');
    setError(null);

    try {
      const ws = new WebSocket(fullUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        setError(null);
        reconnectCountRef.current = 0;

        // Subscribe to station-specific weights if stationId provided
        if (stationId) {
          ws.send(JSON.stringify({ type: 'subscribe', station_id: stationId }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const message: ScaleMessage = JSON.parse(event.data);

          if (message.type === 'weight') {
            const weightMsg = message as ScaleWeightMessage;

            // Filter by station ID if specified
            if (stationId && weightMsg.station_id !== stationId) {
              return; // Ignore weights from other stations
            }

            setWeight(weightMsg.weight);
            setUnit(weightMsg.unit || 'lb');
            setLastUpdate(new Date());
            checkStability(weightMsg.weight);
          } else if (message.type === 'print_trigger') {
            // Scale's physical Print button was pressed — invoke callback
            const triggerMsg = message as ScaleWeightMessage;
            if (stationId && triggerMsg.station_id !== stationId) return;
            onPrintTriggerRef.current?.(triggerMsg.weight, triggerMsg.unit || 'lb');
          } else if (message.type === 'ack') {
            // Handle acknowledgments (for tare commands, etc.)
            const ackMsg = message as ScaleAckMessage;
            if (ackMsg.status === 'error') {
              console.warn('[useScaleWeight] Ack error:', ackMsg.error);
            }
          }
        } catch (err) {
          console.error('[useScaleWeight] Failed to parse message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('[useScaleWeight] WebSocket error:', event);
        setError('Connection error');
        setStatus('error');
      };

      ws.onclose = (event) => {
        setStatus('disconnected');
        wsRef.current = null;

        // Only attempt reconnection if component is still mounted
        if (!isMountedRef.current) {
          return;
        }

        // Attempt reconnection
        if (reconnectCountRef.current < reconnectAttempts) {
          reconnectCountRef.current++;
          reconnectTimeoutRef.current = setTimeout(connect, reconnectInterval);
        } else {
          setError('Connection lost. Max reconnection attempts reached.');
        }
      };
    } catch (err) {
      console.error('[useScaleWeight] Failed to create WebSocket:', err);
      setError('Failed to connect to scale');
      setStatus('error');
    }
  }, [stationId, reconnectAttempts, reconnectInterval, checkStability]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {

    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnection counter
    reconnectCountRef.current = reconnectAttempts; // Prevent auto-reconnect

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setStatus('disconnected');
  }, [reconnectAttempts]);

  // Send tare command to scale
  const tare = useCallback(() => {
    // Immediately set weight to 0 for instant UI feedback
    setWeight(0);
    lastWeightsRef.current = []; // Clear stability readings
    setIsStable(true);

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'tare',
        station_id: stationId,
      }));
    } else {
      console.warn('[useScaleWeight] Cannot tare - not connected');
    }
  }, [stationId]);

  // Request simulated weight from scale (sends targetWeight, receives random weight within tolerance)
  const requestWeight = useCallback((targetWeight: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'simulate',
        targetWeight,
        station_id: stationId || 'frontend',
      }));
    } else {
      console.warn('[useScaleWeight] Cannot request weight - not connected');
    }
  }, [stationId]);

  // Send PMW ABS range to scale display (Over/Under checkweighing)
  const setWeightRange = useCallback((low: number, high: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'setWeightRange',
        low,
        high,
        unit: 'lb',
        station_id: stationId,
      }));
    }
  }, [stationId]);

  // Clear PMW ABS range on scale display
  const clearWeightRange = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'clearWeightRange',
        station_id: stationId,
      }));
    }
  }, [stationId]);

  // Subscribe to scale's physical Print button (ST 1)
  const enablePrintButton = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'enablePrintButton',
        station_id: stationId,
      }));
    }
  }, [stationId]);

  // Unsubscribe from scale's Print button (ST 0)
  const disablePrintButton = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'disablePrintButton',
        station_id: stationId,
      }));
    }
  }, [stationId]);

  // Set a preset tare value on the scale (TA command) — scale display shows net weight
  const setPresetTare = useCallback((weight: number, unit: string = 'lb') => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'setPresetTare',
        weight,
        unit,
        station_id: stationId,
      }));
    }
  }, [stationId]);

  // Clear preset tare on the scale (TAC command) — scale reverts to gross weight
  const clearPresetTare = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'command',
        command: 'clearPresetTare',
        station_id: stationId,
      }));
    }
  }, [stationId]);

  // Send an arbitrary JSON message to the scale gateway WebSocket
  const sendMessage = useCallback((msg: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    isMountedRef.current = true;

    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]);

  return {
    weight,
    unit,
    status,
    lastUpdate,
    error,
    connect,
    disconnect,
    tare,
    isStable,
    requestWeight,
    sendMessage,
    setWeightRange,
    clearWeightRange,
    enablePrintButton,
    disablePrintButton,
    setPresetTare,
    clearPresetTare,
  };
}

export default useScaleWeight;
